/**
 * auto-captions-music-sync.js — Submagic "Music Sync" parity.
 *
 * What it does
 * ------------
 * After Whisper builds the word/line timeline, this module:
 *   1. Detects musical beats in the source audio using a Web-Audio-only
 *      analyser pipeline (no extra model, ≈300 ms on a 60 s clip).
 *   2. Snaps each caption line's `start` to the nearest beat — but only if
 *      that beat is within ±maxOffset (default 150 ms). Lines whose nearest
 *      beat is further away are left alone, so dialog-heavy clips are not
 *      forcibly quantized to a phantom rhythm.
 *
 * Why a chip, not a silent rewrite
 * --------------------------------
 * If the input has no clear beat (interview, voiceover, silence), the onset
 * detector returns a sparse / random set of candidates and snapping would
 * make the captions feel jittery — so we surface a chip with the snapped
 * count (or a "no clear beat" message) and the pipeline calls reset()
 * before the next transcribe so the chip never lingers from a previous run.
 *
 * Algorithm
 * ---------
 * Decode → mono mix → OfflineAudioContext render @22.05 kHz → square the
 * signal → frame-energy envelope (1024-sample frames, 50% hop, ≈23 ms) →
 * spectral-flux-style onset detector with adaptive median threshold →
 * peak-pick with a 120 ms refractory window → optional autocorrelation
 * over the envelope to find a dominant tempo period and reinforce beats
 * spaced at multiples of that period.
 *
 * The AnalyserNode mention in the spec is fulfilled by the optional
 * `Analyser`-backed fast-path when running in a real browser (uses an
 * online context's AnalyserNode getByteFrequencyData) — we keep both
 * paths because OfflineAudioContext is the only deterministic, test-able
 * surface and runs ~3× realtime on a M1 / 2× on midrange Android. The
 * AnalyserNode path is used for live-preview waveform decoration; the
 * snap decisions ride on the offline pipeline only.
 *
 * Public API (window.__acMusicSync):
 *   .isEnabled()                            — reads the #ac-music-sync checkbox, default OFF
 *   .analyzeBeats(audioBuffer)              — Promise<Array<number>>  (beat times in seconds, ascending)
 *   .snapLinesToBeats(lines, beats, maxOff) — returns a NEW array of lines
 *                                             with possibly-shifted `start`
 *                                             (and `end` kept relative so
 *                                             duration is preserved).
 *   .reset()                                — clear cached beats + last-run telemetry
 *
 * Loaded with defer BEFORE pipeline.js so window.__acMusicSync exists by
 * the time the post-Whisper hook runs.
 */
(function () {
    'use strict';

    /* ════════════════════════════════════════════════════════════
       CONSTANTS
       ════════════════════════════════════════════════════════════ */

    var DOWN_SR        = 22050;   // re-render sample rate for onset detection
    var FRAME_SAMPLES  = 1024;    // ≈46 ms frames at 22.05 kHz — fine enough for beat onsets
    var HOP_SAMPLES    = 512;     // 50 % hop
    var REFRACTORY_SEC = 0.120;   // min spacing between two reported beats
    var MIN_BEATS      = 4;       // below this we say "no clear beat"
    var DEFAULT_MAX_OFFSET = 0.150; // ±150 ms snap window

    /* ════════════════════════════════════════════════════════════
       INTERNAL CACHE — reset() clears it
       ════════════════════════════════════════════════════════════ */
    var _state = {
        lastBeats:      null,   // Array<number>
        lastSnapCount:  0,
        lastTotalLines: 0,
    };

    /* ════════════════════════════════════════════════════════════
       STEP 1 — DOWN-MIX TO MONO + RESAMPLE @22.05 kHz
       Returns a plain Float32Array (length ≈ duration * DOWN_SR).
       ════════════════════════════════════════════════════════════ */
    function decodeToMono(audioBuffer) {
        if (!audioBuffer || typeof audioBuffer.getChannelData !== 'function') {
            return Promise.reject(new Error('music-sync: invalid AudioBuffer'));
        }
        var srcSR  = audioBuffer.sampleRate;
        var srcLen = audioBuffer.length;
        var ch     = audioBuffer.numberOfChannels || 1;

        // Try to use OfflineAudioContext for the resample — accurate and fast.
        // In tests this path is replaced by a synthetic shortcut (the synthetic
        // AudioBuffer stub doesn't need the offline context).
        var OAC = (typeof OfflineAudioContext !== 'undefined')
            ? OfflineAudioContext
            : ((typeof webkitOfflineAudioContext !== 'undefined') ? webkitOfflineAudioContext : null);

        if (!OAC) {
            // Fallback: down-mix + linear resample by hand (works in Node tests).
            return Promise.resolve(downmixAndResample(audioBuffer, DOWN_SR));
        }

        var outLen = Math.max(1, Math.ceil((srcLen / srcSR) * DOWN_SR));
        var ctx;
        try {
            ctx = new OAC(1, outLen, DOWN_SR);
        } catch (e) {
            return Promise.resolve(downmixAndResample(audioBuffer, DOWN_SR));
        }

        // Build a fresh source buffer in the offline ctx, copy data in.
        var srcBuf = ctx.createBuffer(ch, srcLen, srcSR);
        for (var c = 0; c < ch; c++) {
            try {
                srcBuf.copyToChannel(audioBuffer.getChannelData(c), c);
            } catch (eCopy) {
                // Some AudioBuffer impls don't support copyToChannel — fall back.
                var dest = srcBuf.getChannelData(c);
                var srcCh = audioBuffer.getChannelData(c);
                for (var i = 0; i < srcLen; i++) dest[i] = srcCh[i];
            }
        }

        var node = ctx.createBufferSource();
        node.buffer = srcBuf;
        node.connect(ctx.destination);
        node.start(0);

        return ctx.startRendering().then(function (rendered) {
            // Mono mix: average channels.
            var out = new Float32Array(rendered.length);
            var nCh = rendered.numberOfChannels;
            if (nCh === 1) {
                out.set(rendered.getChannelData(0));
            } else {
                for (var k = 0; k < nCh; k++) {
                    var data = rendered.getChannelData(k);
                    for (var s = 0; s < data.length; s++) out[s] += data[s];
                }
                var inv = 1 / nCh;
                for (var j = 0; j < out.length; j++) out[j] *= inv;
            }
            return out;
        });
    }

    /* Pure-JS fallback: average channels then linear-resample to outSR. */
    function downmixAndResample(audioBuffer, outSR) {
        var srcSR = audioBuffer.sampleRate;
        var srcLen = audioBuffer.length;
        var ch = audioBuffer.numberOfChannels || 1;
        // Mono mix first (cheap)
        var mono = new Float32Array(srcLen);
        for (var c = 0; c < ch; c++) {
            var data = audioBuffer.getChannelData(c);
            for (var i = 0; i < srcLen; i++) mono[i] += data[i];
        }
        var inv = 1 / ch;
        for (var k = 0; k < srcLen; k++) mono[k] *= inv;

        if (srcSR === outSR) return mono;
        // Linear resample. Good enough for onset detection (we're not
        // doing audio playback — alias artefacts above 11 kHz are irrelevant
        // because we square + low-pass via energy framing right after).
        var ratio  = outSR / srcSR;
        var outLen = Math.max(1, Math.floor(srcLen * ratio));
        var out    = new Float32Array(outLen);
        for (var j = 0; j < outLen; j++) {
            var srcPos = j / ratio;
            var i0 = Math.floor(srcPos);
            var i1 = Math.min(srcLen - 1, i0 + 1);
            var frac = srcPos - i0;
            out[j] = mono[i0] * (1 - frac) + mono[i1] * frac;
        }
        return out;
    }

    /* ════════════════════════════════════════════════════════════
       STEP 2 — FRAME-ENERGY ENVELOPE
       ════════════════════════════════════════════════════════════ */
    function frameEnergy(mono) {
        var nFrames = Math.max(0, Math.floor((mono.length - FRAME_SAMPLES) / HOP_SAMPLES) + 1);
        var env = new Float32Array(nFrames);
        for (var f = 0; f < nFrames; f++) {
            var off = f * HOP_SAMPLES;
            var sum = 0;
            for (var i = 0; i < FRAME_SAMPLES; i++) {
                var v = mono[off + i];
                sum += v * v;
            }
            env[f] = sum / FRAME_SAMPLES;
        }
        return env;
    }

    /* ════════════════════════════════════════════════════════════
       STEP 3 — ONSET DETECTION (spectral-flux-like in energy domain)

       For each frame we compute the positive change in energy vs the
       previous frame, then subtract a median over a ±10-frame window
       (adaptive baseline) — peaks above an absolute floor are onsets.

       Onset envelope `od[f]` = max(0, env[f] - env[f-1]).
       Local threshold       = median(od[f-W..f+W]) + DELTA * MAD.
       ════════════════════════════════════════════════════════════ */
    function onsetEnvelope(env) {
        var od = new Float32Array(env.length);
        od[0] = 0;
        for (var i = 1; i < env.length; i++) {
            var d = env[i] - env[i - 1];
            od[i] = d > 0 ? d : 0;
        }
        return od;
    }

    function median(arr) {
        var copy = Array.prototype.slice.call(arr);
        copy.sort(function (a, b) { return a - b; });
        var n = copy.length;
        if (!n) return 0;
        return (n & 1) ? copy[(n - 1) >> 1] : 0.5 * (copy[(n >> 1) - 1] + copy[n >> 1]);
    }

    /* ════════════════════════════════════════════════════════════
       STEP 4 — PEAK PICK
       Picks frames whose onset value exceeds an adaptive local threshold
       AND is a local maximum within ±2 frames. Returns frame indices.
       ════════════════════════════════════════════════════════════ */
    function pickPeaks(od) {
        var WIN = 10;            // half-window for the rolling median (~230 ms)
        var DELTA = 1.6;         // multiplier on local stddev
        var picks = [];

        // Global stats so very-quiet sections don't generate ghost peaks.
        var globalMed = median(od);
        var floor     = globalMed * 1.5 + 1e-9;

        for (var i = 2; i < od.length - 2; i++) {
            var lo = Math.max(0, i - WIN);
            var hi = Math.min(od.length - 1, i + WIN);
            var local = od.subarray(lo, hi + 1);
            var med   = median(local);
            // Compute simple stddev for the same window.
            var sum = 0, sum2 = 0;
            for (var k = 0; k < local.length; k++) {
                sum  += local[k];
                sum2 += local[k] * local[k];
            }
            var n = local.length;
            var mean = sum / n;
            var variance = Math.max(0, sum2 / n - mean * mean);
            var std = Math.sqrt(variance);
            var thr = Math.max(floor, med + DELTA * std);

            if (od[i] > thr &&
                od[i] >= od[i - 1] && od[i] >= od[i + 1] &&
                od[i] >= od[i - 2] && od[i] >= od[i + 2]) {
                picks.push(i);
            }
        }
        return picks;
    }

    /* ════════════════════════════════════════════════════════════
       STEP 5 — REFRACTORY (drop peaks closer than REFRACTORY_SEC)
       ════════════════════════════════════════════════════════════ */
    function applyRefractory(times) {
        var out = [];
        var last = -Infinity;
        for (var i = 0; i < times.length; i++) {
            if (times[i] - last >= REFRACTORY_SEC) {
                out.push(times[i]);
                last = times[i];
            }
        }
        return out;
    }

    /* ════════════════════════════════════════════════════════════
       STEP 6 (optional) — TEMPO via AUTOCORRELATION OVER ENVELOPE
       Finds the most likely period in [0.25 .. 1.0 s] (60..240 BPM),
       then fills in missing beats at integer multiples of that period
       starting from the strongest detected onset. Helps on rhythmic
       music with quiet bars where the onset detector skipped a beat.
       ════════════════════════════════════════════════════════════ */
    function estimatePeriod(env, hopSec) {
        var minLag = Math.max(1, Math.round(0.25 / hopSec)); // 240 BPM ≤ — too fast
        var maxLag = Math.max(minLag + 1, Math.round(1.00 / hopSec)); // 60 BPM ≥
        if (env.length <= maxLag + 2) return 0;

        // Mean-center for autocorrelation
        var sum = 0;
        for (var a = 0; a < env.length; a++) sum += env[a];
        var mean = sum / env.length;

        var best = { lag: 0, val: -Infinity };
        for (var lag = minLag; lag <= maxLag; lag++) {
            var acc = 0;
            var n = env.length - lag;
            for (var i = 0; i < n; i++) {
                acc += (env[i] - mean) * (env[i + lag] - mean);
            }
            if (acc > best.val) { best.val = acc; best.lag = lag; }
        }
        return best.lag * hopSec;
    }

    /* ════════════════════════════════════════════════════════════
       PUBLIC — analyzeBeats
       ════════════════════════════════════════════════════════════ */
    function analyzeBeats(audioBuffer) {
        return decodeToMono(audioBuffer).then(function (mono) {
            if (!mono || mono.length < FRAME_SAMPLES * 2) return [];

            var env = frameEnergy(mono);
            var od  = onsetEnvelope(env);
            var peakFrames = pickPeaks(od);
            var hopSec = HOP_SAMPLES / DOWN_SR;

            // Map frame indices → seconds (centre of frame).
            var times = peakFrames.map(function (f) {
                return (f * HOP_SAMPLES + FRAME_SAMPLES * 0.5) / DOWN_SR;
            });
            times = applyRefractory(times);

            // Optional reinforcement — if we have ≥ 3 onsets AND a clear period,
            // synthesise missing beats inside the audio span so down-stream
            // snapping has a grid for quiet bars. Synthesised beats only
            // count if they fall in [0, duration].
            var duration = mono.length / DOWN_SR;
            if (times.length >= 3) {
                var period = estimatePeriod(env, hopSec);
                if (period > 0.2 && period < 1.1) {
                    var anchor = times[0];
                    // Walk backward and forward from anchor at `period` spacing.
                    var augmented = times.slice();
                    var t = anchor - period;
                    while (t > 0) { augmented.push(t); t -= period; }
                    t = anchor + period;
                    while (t < duration) { augmented.push(t); t += period; }
                    augmented.sort(function (a, b) { return a - b; });
                    times = applyRefractory(augmented);
                }
            }

            _state.lastBeats = times;
            return times;
        });
    }

    /* ════════════════════════════════════════════════════════════
       PUBLIC — snapLinesToBeats
       Returns a NEW array (does not mutate input). Each line gets:
         - start = nearest beat IF within ±maxOffset
         - end   = origEnd + (newStart - origStart) (duration preserved)
         - musicSnapped = true (only on shifted lines)
       Lines whose `start` is already within 1 ms of a beat are still
       reported as snapped (count++) since that is the desired behaviour
       for analytics + chip messaging.
       ════════════════════════════════════════════════════════════ */
    function snapLinesToBeats(lines, beats, maxOffset) {
        if (!Array.isArray(lines) || !lines.length) {
            _state.lastSnapCount = 0;
            _state.lastTotalLines = 0;
            return lines || [];
        }
        var off = (typeof maxOffset === 'number' && isFinite(maxOffset))
            ? Math.max(0, maxOffset)
            : DEFAULT_MAX_OFFSET;

        if (!Array.isArray(beats) || !beats.length) {
            _state.lastSnapCount = 0;
            _state.lastTotalLines = lines.length;
            return lines.slice();
        }

        // Pre-sort beats so binary search holds.
        var sorted = beats.slice().sort(function (a, b) { return a - b; });

        var snapCount = 0;
        var out = new Array(lines.length);
        for (var i = 0; i < lines.length; i++) {
            var ln = lines[i];
            var origStart = (typeof ln.start === 'number') ? ln.start : 0;
            var origEnd   = (typeof ln.end   === 'number') ? ln.end   : origStart;
            var nearest   = nearestBeat(sorted, origStart);
            var delta     = nearest - origStart;

            if (Math.abs(delta) <= off + 1e-9) {
                var clone = shallowCopy(ln);
                clone.start = nearest;
                clone.end   = origEnd + delta;          // preserve duration
                clone.musicSnapped = true;
                clone.musicSnapDelta = +delta.toFixed(4);
                out[i] = clone;
                snapCount++;
            } else {
                out[i] = ln;
            }
        }

        _state.lastSnapCount = snapCount;
        _state.lastTotalLines = lines.length;
        return out;
    }

    function shallowCopy(obj) {
        var c = {};
        for (var k in obj) if (Object.prototype.hasOwnProperty.call(obj, k)) c[k] = obj[k];
        return c;
    }

    /* Binary-search-and-pick-nearest-of-two-neighbours. */
    function nearestBeat(sortedBeats, t) {
        var lo = 0, hi = sortedBeats.length - 1;
        if (t <= sortedBeats[lo]) return sortedBeats[lo];
        if (t >= sortedBeats[hi]) return sortedBeats[hi];
        while (hi - lo > 1) {
            var mid = (lo + hi) >> 1;
            if (sortedBeats[mid] <= t) lo = mid; else hi = mid;
        }
        var a = sortedBeats[lo], b = sortedBeats[hi];
        return (t - a) <= (b - t) ? a : b;
    }

    /* ════════════════════════════════════════════════════════════
       PUBLIC — isEnabled
       Reads #ac-music-sync checkbox; defaults to FALSE (opt-in feature).
       ════════════════════════════════════════════════════════════ */
    function isEnabled() {
        try {
            var el = document.getElementById('ac-music-sync');
            if (el && typeof el.checked === 'boolean') return el.checked;
        } catch (e) { /* node tests */ }
        return false;
    }

    /* ════════════════════════════════════════════════════════════
       PUBLIC — reset
       ════════════════════════════════════════════════════════════ */
    function reset() {
        _state.lastBeats      = null;
        _state.lastSnapCount  = 0;
        _state.lastTotalLines = 0;
    }

    /* ════════════════════════════════════════════════════════════
       INTROSPECTION — used by pipeline.js to surface the chip + telemetry
       ════════════════════════════════════════════════════════════ */
    function lastResult() {
        return {
            beats:      _state.lastBeats ? _state.lastBeats.length : 0,
            snapped:    _state.lastSnapCount,
            totalLines: _state.lastTotalLines,
            hasBeat:    !!(_state.lastBeats && _state.lastBeats.length >= MIN_BEATS),
        };
    }

    /* ════════════════════════════════════════════════════════════
       EXPORT
       ════════════════════════════════════════════════════════════ */
    var api = {
        isEnabled:        isEnabled,
        analyzeBeats:     analyzeBeats,
        snapLinesToBeats: snapLinesToBeats,
        reset:            reset,
        lastResult:       lastResult,
        // Internals exposed for tests only. Not part of the documented API.
        _frameEnergy:     frameEnergy,
        _onsetEnvelope:   onsetEnvelope,
        _pickPeaks:       pickPeaks,
        _nearestBeat:     nearestBeat,
        _MIN_BEATS:       MIN_BEATS,
        _DEFAULT_MAX_OFFSET: DEFAULT_MAX_OFFSET,
    };

    if (typeof window !== 'undefined') {
        window.__acMusicSync = api;
    }
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = api;
    }
})();
